// Camera modes + per-mode parameters for the Orientation Editor's 3D pane,
// mirroring the Unreal client's camera panel (UTurboTwinCameraPanelWidget) —
// the same labels, ranges, order, defaults, and the controls cheat-sheet.
//
// The viewer speaks `setCameraMode { mode, params? }`. `params` is
// INTEGER-ENCODED (the bridge drops fractional floats): speeds as mm/s (`*Mm`),
// everything else as value×1000 (`*Milli`), booleans as-is. Defaults + Reset
// targets are the Unreal baselines.

export type CameraModeKey = 'freeLook' | 'freeOrbit' | 'walk';

/** Integer-encoded param bag over the bridge (matches bevy `CameraParams`). */
export interface CameraParamsWire {
  freeLookSpeedMm?: number;
  lookSensMilli?: number;
  freeLookScrollMoveScaleMilli?: number;
  orbitRotateMilli?: number;
  orbitZoomMilli?: number;
  orbitPanMilli?: number;
  orbitSmoothingMilli?: number;
  orbitPivotDot?: boolean;
  walkSpeedMm?: number;
  walkGravityMilli?: number;
}

/** Per-mode UI values, in the Unreal panel's display units. */
export interface CameraModeValues {
  freeLook: { moveSpeed: number; lookSens: number; scrollMoveScale: number };
  freeOrbit: { pivotDot: boolean; pan: number; rotate: number; zoom: number; smoothing: number };
  walk: { walkSpeed: number; lookSens: number; gravity: number };
}

/** Unreal baselines = defaults = Reset targets. */
export const CAMERA_DEFAULTS: CameraModeValues = {
  freeLook: { moveSpeed: 24, lookSens: 1.0, scrollMoveScale: 0.5 },
  freeOrbit: { pivotDot: true, pan: 1.0, rotate: 1.0, zoom: 0.15, smoothing: 3.0 },
  walk: { walkSpeed: 12, lookSens: 1.0, gravity: 2.5 },
};

// The viewer's Free Orbit sensitivities are multipliers on the crate baseline
// (baseline = 1.0×). The Unreal Zoom Sensitivity is an absolute value whose
// baseline is 0.15, so we send value/0.15 to keep "0.15 == neutral".
const ORBIT_ZOOM_BASE = 0.15;
const int = (v: number) => Math.round(v);

/** A control in a mode's settings column: a slider or a toggle. */
export type Control =
  | {
      kind: 'slider';
      key: string;
      label: string;
      min: number;
      max: number;
      step: number;
      /** Value is a speed in m/s; the readout follows the units preference. */
      speed?: boolean;
      encode: (v: number) => [keyof CameraParamsWire, number];
    }
  | {
      kind: 'toggle';
      key: string;
      label: string;
      encode: (v: boolean) => [keyof CameraParamsWire, boolean];
    };

/** One Action → Keys row for the right-hand controls cheat-sheet. */
export interface CheatRow {
  action: string;
  keys: string;
}

export const CAMERA_MODE_LABEL: Record<CameraModeKey, string> = {
  freeLook: 'Free Look',
  freeOrbit: 'Free Orbit',
  walk: 'Walk',
};

/** One-line hover description per mode (mode buttons + controls popover). */
export const CAMERA_MODE_DESC: Record<CameraModeKey, string> = {
  freeLook: 'Fly freely like a drone: WASD moves, hold right mouse to look around.',
  freeOrbit: 'Orbit around a focus point: hold right mouse to orbit, middle mouse pans, wheel zooms.',
  walk: 'Walk on the ground in first person: WASD walks, Space jumps, Shift runs.',
};

/** Switcher order matches Unreal: Free Look, Free Orbit, Walk. */
export const CAMERA_MODE_ORDER: CameraModeKey[] = ['freeLook', 'freeOrbit', 'walk'];

export const CAMERA_MODES: {
  [M in CameraModeKey]: { controls: Control[]; cheats: CheatRow[] };
} = {
  freeLook: {
    controls: [
      { kind: 'slider', key: 'moveSpeed', label: 'Move Speed', min: 1, max: 500, step: 1, speed: true, encode: (v) => ['freeLookSpeedMm', int(v * 1000)] },
      { kind: 'slider', key: 'lookSens', label: 'Look Sensitivity', min: 0.1, max: 5, step: 0.1, encode: (v) => ['lookSensMilli', int(v * 1000)] },
      { kind: 'slider', key: 'scrollMoveScale', label: 'Dolly Scale', min: 0.05, max: 2, step: 0.05, encode: (v) => ['freeLookScrollMoveScaleMilli', int(v * 1000)] },
    ],
    cheats: [
      { action: 'Move', keys: 'W  A  S  D' },
      { action: 'Up / Down', keys: 'E / Q' },
      { action: 'Look Around', keys: 'Right-Mouse + Drag' },
      { action: 'Dolly', keys: 'Mouse Wheel' },
      { action: 'Adjust Speed', keys: '+ / −  (or Shift + Wheel)' },
    ],
  },
  freeOrbit: {
    controls: [
      // Pivot Dot: the orbit-focus marker. As of the TurboTwin 9b18ae4 /bevy
      // stamp it renders as a solid camera-facing dot (was a wireframe sphere).
      { kind: 'toggle', key: 'pivotDot', label: 'Pivot Dot', encode: (v) => ['orbitPivotDot', v] },
      { kind: 'slider', key: 'pan', label: 'Pan Sensitivity', min: 0.1, max: 5, step: 0.1, encode: (v) => ['orbitPanMilli', int(v * 1000)] },
      { kind: 'slider', key: 'rotate', label: 'Rotation Sensitivity', min: 0.1, max: 5, step: 0.1, encode: (v) => ['orbitRotateMilli', int(v * 1000)] },
      { kind: 'slider', key: 'zoom', label: 'Zoom Sensitivity', min: 0.02, max: 0.5, step: 0.01, encode: (v) => ['orbitZoomMilli', int((v / ORBIT_ZOOM_BASE) * 1000)] },
      { kind: 'slider', key: 'smoothing', label: 'Smoothing', min: 1, max: 10, step: 0.5, encode: (v) => ['orbitSmoothingMilli', int(v * 1000)] },
    ],
    cheats: [
      { action: 'Orbit', keys: 'Right-Mouse + Drag' },
      { action: 'Pan Pivot', keys: 'Middle-Mouse + Drag' },
      { action: 'Zoom', keys: 'Mouse Wheel' },
    ],
  },
  walk: {
    controls: [
      { kind: 'slider', key: 'walkSpeed', label: 'Walk Speed', min: 1, max: 20, step: 0.5, speed: true, encode: (v) => ['walkSpeedMm', int(v * 1000)] },
      { kind: 'slider', key: 'lookSens', label: 'Look Sensitivity', min: 0.1, max: 5, step: 0.1, encode: (v) => ['lookSensMilli', int(v * 1000)] },
      { kind: 'slider', key: 'gravity', label: 'Gravity Scale', min: 0.5, max: 5, step: 0.1, encode: (v) => ['walkGravityMilli', int(v * 1000)] },
    ],
    cheats: [
      { action: 'Look', keys: 'Click, then Mouse  (Esc frees)' },
      { action: 'Walk', keys: 'W  A  S  D' },
      { action: 'Jump', keys: 'Space' },
      { action: 'Run', keys: 'Shift' },
    ],
  },
};

/** Encode the active mode's values into the integer wire bag. */
export function encodeParams(mode: CameraModeKey, values: CameraModeValues): CameraParamsWire {
  const out: CameraParamsWire = {};
  const modeVals = values[mode] as Record<string, number | boolean>;
  for (const c of CAMERA_MODES[mode].controls) {
    if (c.kind === 'slider') {
      const [field, n] = c.encode(modeVals[c.key] as number);
      (out as Record<string, number>)[field] = n;
    } else {
      const [field, b] = c.encode(modeVals[c.key] as boolean);
      (out as Record<string, boolean>)[field] = b;
    }
  }
  return out;
}

const LS_KEY = 'oe.cameraParams.v2';

/** Load persisted per-author values, merged over the Unreal defaults; never throws. */
export function loadCameraValues(): CameraModeValues {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return structuredClone(CAMERA_DEFAULTS);
    const saved = JSON.parse(raw);
    return {
      freeLook: { ...CAMERA_DEFAULTS.freeLook, ...saved.freeLook },
      freeOrbit: { ...CAMERA_DEFAULTS.freeOrbit, ...saved.freeOrbit },
      walk: { ...CAMERA_DEFAULTS.walk, ...saved.walk },
    };
  } catch {
    return structuredClone(CAMERA_DEFAULTS);
  }
}

/** Persist per-author values; never throws. */
export function saveCameraValues(values: CameraModeValues): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(values));
  } catch {
    /* storage unavailable — session values still drive the viewer */
  }
}
